/**
 * V190.1: Subscription Product Configuration
 * V213.1: Paddle fully removed - Google Play only on Android
 * V282.1: Unified to single product ID: verified_monthly
 *
 * Single source of truth for BlissNet subscription products.
 * We have exactly ONE subscription product: BlissNet Verified - $10/month
 *
 * This product is available through Google Play (Android) and Apple Pay (iOS).
 * Internally we always use the same product code: 'verified_monthly'
 *
 * CRITICAL: Google Play Console product ID is 'verified_monthly' with base plan 'monthly'
 */

// ============================================================================
// INTERNAL PRODUCT CODE (Single Source of Truth)
// ============================================================================

/**
 * The one and only internal product code for BlissNet Verified subscription.
 * This is what we store in user_subscriptions.product_code regardless of provider.
 *
 * UNIFIED: Now matches Google Play product ID to eliminate mapping complexity.
 *
 * @constant
 */
export const VERIFIED_PRODUCT_CODE = 'verified_monthly' as const;

/**
 * Google Play Billing product ID for BlissNet Verified monthly subscription.
 *
 * This is the product ID configured in Google Play Console.
 * Falls back to 'verified_monthly' (the actual Play Console product ID).
 *
 * @see https://play.google.com/console
 */
export const GOOGLE_PLAY_VERIFIED_PRODUCT_ID =
  import.meta.env.VITE_GOOGLE_PLAY_VERIFIED_PRODUCT_ID ?? VERIFIED_PRODUCT_CODE;

/**
 * Apple App Store product ID for BlissNet Verified monthly subscription.
 *
 * iOS App Store subscription product ID must match App Store Connect exactly.
 *
 * This is the product ID configured in App Store Connect.
 * Falls back to standard naming convention if env var is not set.
 *
 * @see https://appstoreconnect.apple.com
 */
export const APPLE_VERIFIED_PRODUCT_ID =
  import.meta.env.VITE_APPLE_VERIFIED_PRODUCT_ID ?? "com.blissnet.verified.monthly";

// ============================================================================
// PRODUCT METADATA
// ============================================================================

/**
 * Product metadata for BlissNet Verified subscription.
 * Used for display purposes and business logic.
 */
export const VERIFIED_PRODUCT_METADATA = {
  name: 'BlissNet Verified',
  description: 'Get verified status and +100 trust score boost',
  price: {
    amount: 10.00,
    currency: 'USD',
    interval: 'month' as const,
  },
  benefits: [
    'Verified badge on your profile',
    '+100 trust score boost',
    'Priority support',
    'Stand out in the network',
  ],
  trustBonus: 100,
} as const;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Supported payment providers for subscriptions.
 * V213.1: Paddle fully removed - Google Play and Apple Pay only
 */
export type SubscriptionProvider = 'google_play' | 'apple';

/**
 * Product code type (currently only one product).
 */
export type ProductCode = typeof VERIFIED_PRODUCT_CODE;

/**
 * Helper function to get provider-specific product ID.
 * 
 * @param provider - The payment provider
 * @returns The provider-specific product/price ID
 */
export function getProviderProductId(provider: SubscriptionProvider): string {
  switch (provider) {
    case 'google_play':
      return GOOGLE_PLAY_VERIFIED_PRODUCT_ID;
    case 'apple':
      return APPLE_VERIFIED_PRODUCT_ID;
    default:
      throw new Error(`Unknown provider: ${provider}`);
  }
}

/**
 * Helper function to validate if a product code is the verified product.
 * 
 * @param productCode - The product code to check
 * @returns True if the product code is the verified product
 */
export function isVerifiedProduct(productCode: string): boolean {
  return productCode === VERIFIED_PRODUCT_CODE;
}

/**
 * Helper function to normalize provider-specific product IDs to internal product code.
 *
 * This is useful when receiving webhook events that might use provider-specific IDs.
 * Maps legacy product IDs to the unified 'verified_monthly' code.
 *
 * @param providerProductId - The provider-specific product/price ID
 * @returns The internal product code
 */
export function normalizeProductCode(providerProductId: string): ProductCode {
  // Map known provider IDs to internal product code
  const knownProviderIds = [
    GOOGLE_PLAY_VERIFIED_PRODUCT_ID,
    APPLE_VERIFIED_PRODUCT_ID,
    // Legacy Google Play product IDs (for backward compatibility)
    'verified_monthly',
    'verified_monthly_10',
    'blissnet_verified_monthly_10',
  ];

  if (knownProviderIds.includes(providerProductId)) {
    return VERIFIED_PRODUCT_CODE;
  }

  // If already using internal code, return as-is
  if (providerProductId === VERIFIED_PRODUCT_CODE) {
    return VERIFIED_PRODUCT_CODE;
  }

  // Default to verified product (we only have one product)
  console.warn(`[V282.1] Unknown provider product ID: ${providerProductId}, defaulting to ${VERIFIED_PRODUCT_CODE}`);
  return VERIFIED_PRODUCT_CODE;
}

